import React, { useContext, useState } from "react";
import {
  Modal,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { styled, useMediaQuery } from "@mui/system";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import GlobalStateContext from "../context/globalStateContext";
import { COLORS } from "../utils/COLORS";
import { firebase, firestore, auth } from "../firebaseConfig";
import GoogleIcon from "@mui/icons-material/Google";
import { toast } from "react-simple-toasts";
import UserContext from "../context/userContext";

const StyledModal = styled(Modal)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const ModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: "#0F172A",
  borderRadius: "12px",
  border: "1px solid #1E293B",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.6)",
  padding: "32px",
  outline: "none",
  width: "400px",
  maxWidth: "90vw",
  textAlign: "center",
}));

const AuthButton = styled(Button)({
  backgroundColor: "#10B981",
  color: "#FFFFFF",
  borderRadius: "8px",
  padding: "12px 24px",
  fontSize: "16px",
  fontWeight: "600",
  textTransform: "none",
  border: 0,
  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
  "&:hover": {
    backgroundColor: "#059669",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.35)",
  },
  "&:disabled": {
    backgroundColor: "#374151",
    color: "#9CA3AF",
  },
});

const GoogleButton = styled(Button)({
  backgroundColor: "#FFFFFF",
  color: "#374151",
  borderRadius: "8px",
  padding: "12px 24px",
  fontSize: "16px",
  fontWeight: "600",
  textTransform: "none",
  border: "1px solid #E5E7EB",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  "&:hover": {
    backgroundColor: "#F9FAFB",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
  "&:disabled": {
    backgroundColor: "#E5E7EB",
    color: "#9CA3AF",
  },
});


const AuthModal = () => {
  const { user, setUser } = useContext(UserContext);
  const { globalState, setGlobalState } = useContext(GlobalStateContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentMode, setCurrentMode] = useState("createAccount");
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  
  const location = useLocation();
  const handleClose = () => {
    if (location.pathname === '/add-animal' && !user) return;
    setGlobalState({ ...globalState, showAuthModal: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentMode === "createAccount") {
        await handleCreateAccount();
      } else {
        await handleSignIn();
      }
      handleClose();
    } catch (error) {
      console.error("Error:", error);
      toast(error.message || "An error occurred. Please try again.");
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      const result = await firebase.auth().signInWithPopup(provider);
      const user = result.user;

      // Check if user exists in Firestore
      const userDoc = await firestore.collection("users").doc(user.uid).get();

      if (!userDoc.exists) {
        // Create new user document
        const newUserData = {
          email: user.email,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          uid: user.uid,
          username: user.displayName || user.email.split("@")[0],
          photoURL: user.photoURL || null,
        };
        await firestore.collection("users").doc(user.uid).set(newUserData);
        setUser({
          ...newUserData,
          createdAt: new Date().toISOString(),
        });
      } else {
        setUser({ id: userDoc.id, ...userDoc.data() });
      }

      toast("Successfully signed in with Google!");
      setGlobalState({ ...globalState, showAuthModal: false });
    } catch (error) {
      console.error("Google sign in error:", error);
      toast(error.message || "Failed to sign in with Google");
    }
    setLoading(false);
  };

  const handleCreateAccount = async () => {
    try {
      const userCredential = await firebase
        .auth()
        .createUserWithEmailAndPassword(email, password);
      const user = userCredential.user;

      // Write user info to Firestore
      await firestore.collection("users").doc(user.uid).set({
        email: user.email,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        uid: user.uid,
        username: username,
      });

      setUser({
        email: user.email,
        createdAt: new Date().toISOString(),
        uid: user.uid,
        username: username,
      });

      toast("Account created successfully!");
      setGlobalState({ ...globalState, showAuthModal: false });
    } catch (error) {
      console.error("Error creating account:", error);
      throw error; // Re-throw the error to be caught in handleSubmit
    }
  };

  const handleSignIn = async () => {
    try {
      await firebase.auth().signInWithEmailAndPassword(email, password);
      const user = firebase.auth().currentUser;
      if (!user?.uid) {
        toast("No user found");
        return;
      }

      const userDoc = await firestore.collection("users").doc(user.uid).get();
      console.log("Sign in - userDoc exists:", userDoc.exists);
      console.log("Sign in - userDoc data:", userDoc.data());
      
      if (!userDoc.exists) {
        // Create user document if it doesn't exist
        const newUserData = {
          email: user.email,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          uid: user.uid,
          username: user.email.split('@')[0],
        };
        
        await firestore.collection("users").doc(user.uid).set(newUserData);
        setUser({
          email: user.email,
          createdAt: new Date().toISOString(),
          uid: user.uid,
          username: user.email.split('@')[0],
        });
        toast("Successfully signed in!");
        setGlobalState({ ...globalState, showAuthModal: false });
        return;
      }
      
      const userDataObj = { id: userDoc.id, ...userDoc.data() };

      setUser(userDataObj);
      toast("Successfully signed in!");
      setGlobalState({ ...globalState, showAuthModal: false });
    } catch (error) {
      console.error("Error signing in:", error);
      if (error.code === "auth/wrong-password") {
        toast("Incorrect password. Please try again.");
      } else {
        toast(
          error.message ||
            "An error occurred while signing in. Please try again."
        );
      }
      throw error; // Re-throw the error to be caught in handleSubmit
    }
  };

  return (
    <StyledModal
      open={globalState.showAuthModal}
      onClose={handleClose}
      BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.5)" } }}
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        onClick={handleClose}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: isMobileView ? "flex-end" : "center", height: "100%" }}
      >
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <Typography variant="h6" gutterBottom sx={{ color: "#FFFFFF", fontWeight: 600, marginBottom: "24px" }}>
            {currentMode === "createAccount" ? "Create Account" : "Sign In"}
          </Typography>
          
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {currentMode === "createAccount" && (
              <TextField
                fullWidth
                label="Username"
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#1E293B",
                    color: "#FFFFFF",
                    "& fieldset": {
                      borderColor: "#374151",
                    },
                    "&:hover fieldset": {
                      borderColor: "#10B981",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#10B981",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#9CA3AF",
                    "&.Mui-focused": {
                      color: "#10B981",
                    },
                  },
                }}
              />
            )}
            
            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#1E293B",
                  color: "#FFFFFF",
                  "& fieldset": {
                    borderColor: "#374151",
                  },
                  "&:hover fieldset": {
                    borderColor: "#10B981",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#10B981",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#9CA3AF",
                  "&.Mui-focused": {
                    color: "#10B981",
                  },
                },
              }}
            />
            
            <TextField
              fullWidth
              label="Password"
              type="password"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#1E293B",
                  color: "#FFFFFF",
                  "& fieldset": {
                    borderColor: "#374151",
                  },
                  "&:hover fieldset": {
                    borderColor: "#10B981",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#10B981",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#9CA3AF",
                  "&.Mui-focused": {
                    color: "#10B981",
                  },
                },
              }}
            />
            
            <AuthButton
              type="submit"
              fullWidth
              disabled={loading || !email || !password || (currentMode === "createAccount" && !username)}
              sx={{ marginTop: "8px" }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : currentMode === "createAccount" ? (
                "Create Account"
              ) : (
                "Sign In"
              )}
            </AuthButton>
          </form>

          <Box sx={{ display: "flex", alignItems: "center", margin: "20px 0" }}>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "#374151" }} />
            <Typography sx={{ padding: "0 16px", color: "#6B7280", fontSize: "14px" }}>or</Typography>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "#374151" }} />
          </Box>

          <GoogleButton
            fullWidth
            onClick={handleGoogleSignIn}
            disabled={loading}
            startIcon={<GoogleIcon />}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Continue with Google"}
          </GoogleButton>

          <Button
            onClick={() =>
              setCurrentMode(
                currentMode === "createAccount" ? "signIn" : "createAccount"
              )
            }
            sx={{ 
              marginTop: "16px",
              color: "#9CA3AF",
              fontSize: "14px",
              textTransform: "none",
              "&:hover": {
                color: "#FFFFFF",
                backgroundColor: "transparent",
              },
            }}
          >
            {currentMode === "createAccount" ? "Already have an account? Sign in" : "Need an account? Create one"}
          </Button>
        </ModalContent>
      </motion.div>
    </StyledModal>
  );
};

export default AuthModal;
